import {
  ArrowDropDown,
  ArrowDropUp,
  Key,
  Lock,
  Photo,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  useLoggedInEmployeeId,
  useLoggedInSupervisorAccess,
  useLoggedInUserRoles,
} from '~/components/Auth';
import { UserProfilePictureDialog } from '~/components/Dialogs/UserProfilePictureDialog';
import { useApplicationConfig } from '~/data/applicationConfig';
import { fetchEmployeeLoginData, useEmployeeLoginData } from '~/data/employee';
import { Roles } from '~/data/user';
import { UseDialogStateOutput } from '~/hooks/useDialogState';
import { useMenu } from '~/hooks/useMenu';
import { AccountManagementMenu_OnUpdateLegacy } from '~/library/AccountManagementMenu/component';
import { ChangeLoginMethodDialog } from '~/scenes/PeopleModule/components/EmployeeActions/ChangeLoginMethodDialog';
import { EmployeeActivityLogDialog } from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog';
import { isRoleHRAdmin, isRoleITAdmin } from '~/services/Permissions';
import { AppDispatch } from '~/store';

import { ChangeAccountStatus } from './ChangeAccountStatus';
import { ChangeRole } from './ChangeRole';
import { ChangeUserName } from './ChangeUserName';
import { ResetPassword } from './ResetPassword';
import { useEmployeeAction } from './useEmployeeActions';
import { useSxStyles } from './useStyles';

export type EmployeeActionsProps = AccountManagementMenu_OnUpdateLegacy & {
  employeeId: number;
};

export const EmployeeActions = (props: EmployeeActionsProps) => {
  const { employeeId, onUpdateLegacy } = props;
  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { isSupervisorWithRoles } = useLoggedInSupervisorAccess(employeeId);

  const { applicationConfig, hasData: applicationConfigLoaded } =
    useApplicationConfig();

  const {
    employeeAdminLoginData,
    loginDataLoading,
    loginDataFailedLoading,
    hasLoginData,
  } = useEmployeeLoginData();

  const { internalSignInEnabled, externalSignInEnabled, userLoginIsExternal } =
    useMemo(() => {
      return {
        internalSignInEnabled:
          applicationConfig.fComConfig?.coreConfig?.fComInternalSignin || false,
        externalSignInEnabled:
          applicationConfig.fComConfig?.coreConfig?.fComInternalSignin || false,
        userLoginIsExternal: employeeAdminLoginData.fLoginIsExternal || false,
      };
    }, [applicationConfig, employeeAdminLoginData]);

  const canChangeLoginMethod = internalSignInEnabled && externalSignInEnabled;
  const canResetInternalPassword =
    internalSignInEnabled &&
    (!externalSignInEnabled || (externalSignInEnabled && !userLoginIsExternal));

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const roles = useLoggedInUserRoles();

  const canViewAccountManagementMenu = useMemo(() => {
    return (
      isRoleITAdmin(roles) ||
      isSupervisorWithRoles(
        [Roles.HR_ADMIN, Roles.HR_ADMIN_LIMITED, Roles.HR_STAFF].filter(
          (role) => roles.includes(role.toString()),
        ),
      )
    );
  }, [roles, isSupervisorWithRoles]);

  const canViewUserActivity =
    employeeId !== loggedInEmployeeId && isRoleHRAdmin(roles);

  const {
    changeRolesDialog,
    changeLoginMethod,
    resetPasswordDialog,
    changeUserNameDialog,
    changeAccountStatusDialog,
    cropImageDialog,
    activityTrackingDialog,
  } = useEmployeeAction();

  const actionsMenu = useMenu();

  useEffect(() => {
    dispatch(fetchEmployeeLoginData({ empId: employeeId }));
  }, [dispatch, employeeId]);

  const loginDataLoaded =
    (!loginDataLoading && !loginDataFailedLoading) || hasLoginData;
  const dataLoaded = loginDataLoaded && applicationConfigLoaded;

  const EmployeeActionsMenuItem = (menuProps: {
    dialogState: UseDialogStateOutput;
    label: string;
    showItem?: boolean;
    icon?: ReactNode;
  }) => {
    const { dialogState, label, showItem, icon } = menuProps;
    const isShown = typeof showItem === 'undefined' || showItem;

    return isShown ? (
      <MenuItem
        onClick={() => {
          actionsMenu.close();
          dialogState.open();
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </MenuItem>
    ) : null;
  };

  const handleCloseDialog = (dialogState: UseDialogStateOutput) => {
    dialogState.close();
  };

  return canViewAccountManagementMenu ? (
    <>
      <Button
        onClick={(event) => {
          if (dataLoaded) {
            actionsMenu.open(event);
          }
        }}
        variant="contained"
        sx={sxStyles.menuButton}
        endIcon={actionsMenu.isOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        disableElevation
      >
        {t('laAccountMgmt')}
      </Button>
      {dataLoaded && (
        <>
          <Menu
            anchorEl={actionsMenu.anchorEl}
            open={actionsMenu.isOpen}
            onClose={() => {
              actionsMenu.close();
            }}
            MenuListProps={{
              sx: sxStyles.menuList,
            }}
          >
            <EmployeeActionsMenuItem
              label={t('laChangeRole')}
              dialogState={changeRolesDialog}
              showItem={!roles.includes(Roles.IT_ADMIN)}
            />
            <EmployeeActionsMenuItem
              label={t('laChangeAccountStatus')}
              dialogState={changeAccountStatusDialog}
              icon={<Lock />}
            />
            <Divider />
            <EmployeeActionsMenuItem
              label={t('laResetPassword')}
              dialogState={resetPasswordDialog}
              icon={<Key />}
              showItem={canResetInternalPassword}
            />
            <EmployeeActionsMenuItem
              label={t('laChangeUsername')}
              dialogState={changeUserNameDialog}
            />
            <EmployeeActionsMenuItem
              label={t('laChangeLoginMethod')}
              dialogState={changeLoginMethod}
              showItem={canChangeLoginMethod}
            />
            <Divider />
            <EmployeeActionsMenuItem
              label={t('laPicture')}
              dialogState={cropImageDialog}
              icon={<Photo />}
            />
            {canViewUserActivity && <Divider />}
            <EmployeeActionsMenuItem
              label={t('userActivityLog')}
              dialogState={activityTrackingDialog}
              showItem={canViewUserActivity}
            />
          </Menu>
          <ChangeRole
            open={changeRolesDialog.isOpen}
            onClose={() => handleCloseDialog(changeRolesDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <ChangeLoginMethodDialog
            employeeId={employeeId}
            open={changeLoginMethod.isOpen}
            onClose={() => handleCloseDialog(changeLoginMethod)}
            onUpdateLegacy={onUpdateLegacy}
          />
          {/* TODO: Add Change Login Method dialog */}
          <ResetPassword
            open={resetPasswordDialog.isOpen}
            onClose={() => handleCloseDialog(resetPasswordDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <ChangeUserName
            open={changeUserNameDialog.isOpen}
            onClose={() => handleCloseDialog(changeUserNameDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <ChangeAccountStatus
            open={changeAccountStatusDialog.isOpen}
            onClose={() => handleCloseDialog(changeAccountStatusDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <UserProfilePictureDialog
            employeeId={employeeId}
            open={cropImageDialog.isOpen}
            onClose={() => handleCloseDialog(cropImageDialog)}
            onUpdateLegacy={onUpdateLegacy}
          />
          <EmployeeActivityLogDialog
            employeeId={employeeId}
            open={activityTrackingDialog.isOpen}
            onClose={() => handleCloseDialog(activityTrackingDialog)}
          />
        </>
      )}
    </>
  ) : null;
};
